import { useEffect, useState } from 'react';

/**
 * Loads Clerk development keys from .env file when running in development mode
 * @returns Object containing Clerk publishable key
 */
export function useClerkDevKeys() {
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadKeys() {
      try {
        const isDev = import.meta.env.DEV;

        // Try the environment-specific key first, then fall back
        let key;
        if (isDev) {
          key = import.meta.env.DEV_VITE_CLERK_PUBLISHABLE_KEY;
          if (!key) {
            // Fallback to standard key if dev-specific key not found
            key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
          }
        } else {
          // Production mode - use standard key
          key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
        }

        // Set publishable key if found
        if (key) {
          setPublishableKey(key);
        } else {
          console.warn('No Clerk publishable key found in environment variables');
          setPublishableKey(null);
        }
      } catch (err) {
        console.error('Error loading Clerk keys:', err);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsLoading(false);
      }
    }

    loadKeys();
  }, []);

  return { publishableKey, isLoading, error };
}

/**
 * Gets all development keys synchronously
 * @returns Development keys object
 */
export function getDevKeys() {
  const isDev = import.meta.env.DEV;
  
  // Try to get environment-specific keys first, then fall back to standard keys
  const clerkKey = isDev
    ? import.meta.env.DEV_VITE_CLERK_PUBLISHABLE_KEY || import.meta.env.VITE_CLERK_PUBLISHABLE_KEY 
    : import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  
  return {
    clerkPublishableKey: clerkKey || '',
    convexUrl: import.meta.env.VITE_CONVEX_URL || '',
    stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
  };
}